import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  addEvalDatasetLoan,
  cloneLoanToDev,
  createEvalDataset,
  fetchEvalDatasets,
  fetchEvalModels,
  startEvalRun,
} from "./api";
import DatasetDialog from "./DatasetDialog";

const CONNECTED_STAGES = ["docSelection", "pcStep1", "pcStep3"];

const TIER_ORDER = ["A", "B", "C", "Other"];
const TIER_LABELS = {
  A: "Tier A — cost-effective, high-volume",
  B: "Tier B — balanced",
  C: "Tier C — premium",
  Other: "Other",
};
const TIER_SHORT = {
  A: "Cost-effective",
  B: "Balanced",
  C: "Premium",
};

const MODES = [
  {
    id: "loan",
    label: "One loan",
    hint: "Check a single loan by organization and loan ID.",
  },
  {
    id: "dataset",
    label: "Golden dataset",
    hint: "Run a saved batch and score it against human-reviewed income.",
  },
  {
    id: "clone",
    label: "Clone from prod",
    hint: "Copy a production loan into dev, then evaluate the copy.",
  },
];

function formatTokens(tokens) {
  if (!Number.isFinite(tokens)) {
    return "—";
  }

  return `${Math.round(tokens / 1000)}k`;
}

function CandidateSelect({ candidates, value, onChange }) {
  return (
    <select
      className="mse-select mse-select--lg"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={!candidates.length}
      aria-label="Candidate model"
    >
      {TIER_ORDER.map((tier) => {
        const tierCandidates = candidates.filter(
          (entry) => (entry.tier || "Other") === tier,
        );
        if (!tierCandidates.length) {
          return null;
        }

        return (
          <optgroup key={tier} label={TIER_LABELS[tier] || tier}>
            {tierCandidates.map((entry) => (
              <option key={entry.id} value={entry.defaultAlias || entry.id}>
                {entry.label} ({entry.provider})
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

/**
 * Baseline and candidate shown facing each other, because the whole page exists
 * to answer "is the right side safe to ship instead of the left side?".
 */
function ModelMatchup({ catalog, catalogError, value, onChange, candidate }) {
  const baseline = catalog?.baseline;

  return (
    <div className="mse-matchup">
      <div className="mse-matchup__side">
        <span className="mse-matchup__role">Production baseline</span>
        {baseline ? (
          <>
            <strong className="mse-matchup__model">{baseline.modelId}</strong>
            <span className="mse-matchup__meta">
              <span className="mse-provider-badge">{baseline.provider}</span>
              <span className="mse-muted-xs">never overridden</span>
            </span>
          </>
        ) : (
          <span className="mse-muted-xs">
            {catalogError || "Loading production default…"}
          </span>
        )}
      </div>

      <span className="mse-matchup__vs" aria-hidden="true">
        vs
      </span>

      <div className="mse-matchup__side mse-matchup__side--candidate">
        <span className="mse-matchup__role">Candidate</span>
        <CandidateSelect
          candidates={catalog?.candidates || []}
          value={value}
          onChange={onChange}
        />
        <span className="mse-matchup__meta">
          {candidate ? (
            <>
              <span className="mse-provider-badge">{candidate.provider}</span>
              {candidate.tier ? (
                <span className={`mse-tier mse-tier--${candidate.tier}`}>
                  {TIER_SHORT[candidate.tier] || candidate.tier}
                </span>
              ) : null}
              <span className="mse-muted-xs">
                {formatTokens(candidate.maxOutputTokens)} max out
              </span>
            </>
          ) : (
            <span className="mse-muted-xs">Pick a candidate model</span>
          )}
        </span>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="mse-field">
      <span className="mse-field__label">{label}</span>
      {children}
      {hint ? <span className="mse-field__hint">{hint}</span> : null}
    </label>
  );
}

/**
 * Starts an eval run. Three explicit modes replace the old single form, whose
 * "loan IDs" box silently matched nothing unless the loan already existed in the
 * checked-in CSV corpus.
 */
export default function RunLauncher({ env, onQueued }) {
  const [mode, setMode] = useState("loan");
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const [candidateModel, setCandidateModel] = useState("gpt-luna");
  const [submitting, setSubmitting] = useState(false);

  const [organizationId, setOrganizationId] = useState("");
  const [loanId, setLoanId] = useState("");

  const [datasets, setDatasets] = useState([]);
  const [datasetId, setDatasetId] = useState("");
  const [limit, setLimit] = useState("");
  const [defaultDevOrgId, setDefaultDevOrgId] = useState("");

  const [prodOrganizationId, setProdOrganizationId] = useState("");
  const [prodLoanId, setProdLoanId] = useState("");
  const [devOrganizationId, setDevOrganizationId] = useState("");
  const [cloneDatasetId, setCloneDatasetId] = useState("");
  const [shouldRunAfterClone, setShouldRunAfterClone] = useState(true);

  const [dialog, setDialog] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);

  const loadDatasets = useCallback(async () => {
    try {
      const result = await fetchEvalDatasets(env);
      if (!result.success) {
        throw new Error(result.reason || "Could not load datasets.");
      }
      setDatasets(result.datasets || []);
      setDefaultDevOrgId(result.defaultDevOrganizationId || "");
      setDevOrganizationId(
        (current) => current || result.defaultDevOrganizationId || "",
      );
    } catch (err) {
      toast.error(err.message);
    }
  }, [env]);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      setCatalogError("");
      try {
        const result = await fetchEvalModels(env);
        if (cancelled) {
          return;
        }
        if (!result.success) {
          throw new Error(result.reason || "Could not load model catalog.");
        }
        setCatalog(result);
        setCandidateModel(
          result.defaultCandidateAlias ||
            result.candidates?.[0]?.defaultAlias ||
            "gpt-luna",
        );
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err.message);
        }
      }
    };

    loadCatalog();
    loadDatasets();

    return () => {
      cancelled = true;
    };
  }, [env, loadDatasets]);

  const candidate = useMemo(() => {
    const candidates = catalog?.candidates || [];

    return (
      candidates.find(
        (entry) => (entry.defaultAlias || entry.id) === candidateModel,
      ) || null
    );
  }, [candidateModel, catalog]);

  const selectedDataset = useMemo(
    () => datasets.find((entry) => entry.id === datasetId) || null,
    [datasetId, datasets],
  );

  const queueRun = async (payload) => {
    const result = await startEvalRun(
      {
        candidateModel,
        stages: CONNECTED_STAGES,
        chainStep3: true,
        ...payload,
      },
      env,
    );
    if (!result.success) {
      throw new Error(result.reason || "Could not queue this run.");
    }
    toast.success(`Queued run ${result.runId}`);
    onQueued?.(result);

    return result;
  };

  const submitSingleLoan = async () => {
    const trimmedOrganizationId = organizationId.trim();
    const trimmedLoanId = loanId.trim();
    if (!trimmedOrganizationId || !trimmedLoanId) {
      throw new Error("Both an organization ID and a loan ID are required.");
    }
    await queueRun({
      targets: [
        { organizationId: trimmedOrganizationId, loanId: trimmedLoanId },
      ],
    });
  };

  const submitDataset = async () => {
    if (!datasetId) {
      throw new Error("Pick a dataset to run.");
    }
    const payload = { datasetId };
    if (limit.trim()) {
      payload.limit = Number(limit.trim());
    }
    await queueRun(payload);
  };

  const submitClone = async () => {
    const trimmedProdOrganizationId = prodOrganizationId.trim();
    const trimmedProdLoanId = prodLoanId.trim();
    const trimmedDevOrganizationId =
      devOrganizationId.trim() || defaultDevOrgId;
    if (!trimmedProdOrganizationId || !trimmedProdLoanId) {
      throw new Error(
        "The production organization ID and loan ID are both required.",
      );
    }
    if (!trimmedDevOrganizationId) {
      throw new Error("A dev organization ID is required to clone into.");
    }

    toast.info(
      "Cloning loan into dev — this copies documents and can take a while.",
    );
    const result = await cloneLoanToDev(
      {
        prodOrganizationId: trimmedProdOrganizationId,
        prodLoanId: trimmedProdLoanId,
        devOrganizationId: trimmedDevOrganizationId,
        datasetId: cloneDatasetId || undefined,
      },
      env,
    );
    if (!result.success) {
      throw new Error(result.reason || "Clone failed.");
    }
    toast.success(`Cloned to dev loan ${result.target.loanId}`);
    if (cloneDatasetId) {
      await loadDatasets();
    }
    if (shouldRunAfterClone) {
      await queueRun({ targets: [result.target] });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "loan") {
        await submitSingleLoan();
      } else if (mode === "dataset") {
        await submitDataset();
      } else {
        await submitClone();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDataset = async (name) => {
    const shouldSeedFromCorpus = Boolean(dialog?.shouldSeedFromCorpus);
    setDialogBusy(true);
    try {
      const result = await createEvalDataset(
        {
          name,
          seedFromCorpus: shouldSeedFromCorpus,
          environment: env === "dev" ? "dev" : "prod",
        },
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not create the dataset.");
      }
      toast.success(
        shouldSeedFromCorpus
          ? `Imported ${result.added} loans into "${name}"`
          : `Created "${name}"`,
      );
      await loadDatasets();
      setDatasetId(result.dataset?.id || "");
      setDialog(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDialogBusy(false);
    }
  };

  const addCurrentLoanToDataset = async () => {
    const trimmedOrganizationId = organizationId.trim();
    const trimmedLoanId = loanId.trim();
    if (!trimmedOrganizationId || !trimmedLoanId) {
      toast.error("Enter an organization ID and loan ID first.");

      return;
    }
    if (!datasetId) {
      toast.error("Pick a dataset to add this loan to.");

      return;
    }

    try {
      const result = await addEvalDatasetLoan(
        datasetId,
        {
          organizationId: trimmedOrganizationId,
          loanId: trimmedLoanId,
        },
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not add the loan.");
      }
      toast.success(`Added to dataset (${result.loanCount} loans)`);
      await loadDatasets();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const activeMode = MODES.find((entry) => entry.id === mode);
  const canSubmit = Boolean(catalog?.candidates?.length) && !submitting;

  const scope = useMemo(() => {
    if (mode === "loan") {
      return loanId.trim() ? "1 loan" : "no loan selected yet";
    }
    if (mode === "dataset") {
      if (!selectedDataset) {
        return "no dataset selected yet";
      }
      const total = selectedDataset.loanCount ?? 0;
      const capped = limit.trim() ? Math.min(Number(limit.trim()), total) : total;

      return `${capped} of ${total} loans in ${selectedDataset.name}`;
    }

    return prodLoanId.trim()
      ? `1 cloned loan${shouldRunAfterClone ? ", evaluated on arrival" : ", clone only"}`
      : "no production loan selected yet";
  }, [limit, loanId, mode, prodLoanId, selectedDataset, shouldRunAfterClone]);

  return (
    <section className="mse-composer">
      <header className="mse-composer__head">
        <h2 className="mse-composer__title">Run an eval</h2>
        <p className="mse-composer__lede">
          Replays the real production checklist flow twice — once on the
          production model, once on the candidate. Loans are never mutated.
        </p>
      </header>

      <div className="mse-modecards" role="radiogroup" aria-label="What to run">
        {MODES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="radio"
            aria-checked={mode === entry.id}
            className={`mse-modecard ${
              mode === entry.id ? "mse-modecard--active" : ""
            }`}
            onClick={() => setMode(entry.id)}
          >
            <span className="mse-modecard__title">{entry.label}</span>
            <span className="mse-modecard__hint">{entry.hint}</span>
          </button>
        ))}
      </div>

      <form onSubmit={submit}>
        <ModelMatchup
          catalog={catalog}
          catalogError={catalogError}
          value={candidateModel}
          onChange={setCandidateModel}
          candidate={candidate}
        />

        <div className="mse-fields">
          {mode === "loan" && (
            <>
              <Field label="Organization ID">
                <input
                  className="mse-input"
                  value={organizationId}
                  onChange={(event) => setOrganizationId(event.target.value)}
                  placeholder="e.g. PMwHyXBItJmdK0E7Ec7t"
                />
              </Field>
              <Field label="Loan ID" hint="The loan document ID in Firestore.">
                <input
                  className="mse-input"
                  value={loanId}
                  onChange={(event) => setLoanId(event.target.value)}
                  placeholder="e.g. PlNvAI4BIlahUfwoKJlc"
                />
              </Field>
              <div className="mse-field mse-field--wide">
                <span className="mse-field__label">
                  Keep this loan for later (optional)
                </span>
                <div className="mse-inline-actions">
                  <select
                    className="mse-select"
                    value={datasetId}
                    onChange={(event) => setDatasetId(event.target.value)}
                    aria-label="Dataset to add this loan to"
                  >
                    <option value="">Choose a dataset…</option>
                    {datasets.map((dataset) => (
                      <option key={dataset.id} value={dataset.id}>
                        {dataset.name} ({dataset.loanCount})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="mse-btn"
                    onClick={addCurrentLoanToDataset}
                  >
                    Add loan
                  </button>
                </div>
                <span className="mse-field__hint">
                  Saved loans can be re-run as a batch from the Golden dataset
                  tab.
                </span>
              </div>
            </>
          )}

          {mode === "dataset" && (
            <>
              <Field label="Dataset">
                <select
                  className="mse-select"
                  value={datasetId}
                  onChange={(event) => setDatasetId(event.target.value)}
                >
                  <option value="">Select a dataset…</option>
                  {datasets.map((dataset) => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name} — {dataset.loanCount} loans
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Loan limit" hint="Leave blank to run every loan.">
                <input
                  className="mse-input"
                  type="number"
                  min="1"
                  value={limit}
                  onChange={(event) => setLimit(event.target.value)}
                  placeholder="All loans"
                />
              </Field>
              <div className="mse-field mse-field--wide">
                <span className="mse-field__label">Manage datasets</span>
                <div className="mse-inline-actions">
                  <button
                    type="button"
                    className="mse-btn"
                    onClick={() =>
                      setDialog({ shouldSeedFromCorpus: false })
                    }
                  >
                    New empty dataset
                  </button>
                  <button
                    type="button"
                    className="mse-btn"
                    onClick={() => setDialog({ shouldSeedFromCorpus: true })}
                  >
                    Import from golden CSV
                  </button>
                </div>
                {!datasets.length && (
                  <span className="mse-field__hint">
                    No datasets yet — importing the golden CSV is the quickest
                    way to get a scoreable batch.
                  </span>
                )}
              </div>
            </>
          )}

          {mode === "clone" && (
            <>
              <Field label="Production organization ID">
                <input
                  className="mse-input"
                  value={prodOrganizationId}
                  onChange={(event) =>
                    setProdOrganizationId(event.target.value)
                  }
                  placeholder="Source org in production"
                />
              </Field>
              <Field label="Production loan ID">
                <input
                  className="mse-input"
                  value={prodLoanId}
                  onChange={(event) => setProdLoanId(event.target.value)}
                  placeholder="Loan to copy"
                />
              </Field>
              <Field
                label="Dev organization ID"
                hint="Defaults to the shared eval org."
              >
                <input
                  className="mse-input"
                  value={devOrganizationId}
                  onChange={(event) => setDevOrganizationId(event.target.value)}
                  placeholder={defaultDevOrgId || "Target org in dev"}
                />
              </Field>
              <Field label="Add the clone to a dataset (optional)">
                <select
                  className="mse-select"
                  value={cloneDatasetId}
                  onChange={(event) => setCloneDatasetId(event.target.value)}
                >
                  <option value="">Do not save</option>
                  {datasets.map((dataset) => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </option>
                  ))}
                </select>
              </Field>
              <label className="mse-checkbox mse-field--wide">
                <input
                  type="checkbox"
                  checked={shouldRunAfterClone}
                  onChange={(event) =>
                    setShouldRunAfterClone(event.target.checked)
                  }
                />
                <span>
                  Run the comparison as soon as the clone finishes
                  <small className="mse-field__hint">
                    Cloning writes only into the dev project, and the backend
                    refuses to run it unless it is pointed at dev or local.
                  </small>
                </span>
              </label>
            </>
          )}
        </div>

        <footer className="mse-composer__footer">
          <p className="mse-runsummary">
            <span className="mse-runsummary__label">Will run</span>
            {scope}
            <span className="mse-runsummary__sep">·</span>
            doc selection, Step 1, Step 3
          </p>
          <button
            type="submit"
            className="mse-btn mse-btn--primary mse-btn--lg"
            disabled={!canSubmit}
          >
            {submitting
              ? "Working…"
              : activeMode?.id === "clone"
                ? "Clone & run"
                : "Run comparison"}
          </button>
        </footer>
      </form>

      <DatasetDialog
        open={Boolean(dialog)}
        shouldSeedFromCorpus={Boolean(dialog?.shouldSeedFromCorpus)}
        busy={dialogBusy}
        onCancel={() => setDialog(null)}
        onConfirm={confirmDataset}
      />
    </section>
  );
}
